/*
  VORYNEX Tilt — lightweight vanilla-JS port of the 21st.dev "Tilt" reference
  (id 1448, motion-primitives). The original uses framer-motion springs; this
  theme has no React runtime, so the same mouse-relative 3D tilt is done with
  a plain CSS transform + a manual lerp toward the pointer target on each
  animation frame — same spring-ish settle, no dependency.

  Opt-in via `data-vpx-tilt` on any element. Desktop/pointer devices only:
  touch has no hover, and a tilt that only ever shows on tap would be a
  broken half-effect rather than a missing one.
*/
(() => {
  const reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduceMotion) return;

  const hasHover = window.matchMedia && window.matchMedia('(hover: hover)').matches;
  if (!hasHover) return;

  const ROTATION_FACTOR = 8;
  const LERP = 0.15;

  const attach = (el) => {
    let targetX = 0;
    let targetY = 0;
    let currentX = 0;
    let currentY = 0;
    let raf = null;
    let rect = null;

    // NOTE: no `transform-style: preserve-3d` here, deliberately. An earlier
    // version set it on every card at attach time, copied from a React
    // reference that needed its CHILDREN positioned in 3D space. This does
    // not: it rotates the card itself, and `perspective()` inside the
    // transform is enough for that. Setting preserve-3d promoted every card
    // on the page to its own compositing layer permanently — 12 layers
    // holding 32 images on the collection page, paid for on every visit
    // whether or not anyone ever hovered. That is what made the live site
    // drag; it is not a micro-optimisation to leave it off.

    const tick = () => {
      currentX += (targetX - currentX) * LERP;
      currentY += (targetY - currentY) * LERP;
      el.style.transform = `perspective(1000px) rotateX(${currentX}deg) rotateY(${currentY}deg)`;

      if (Math.abs(targetX - currentX) > 0.01 || Math.abs(targetY - currentY) > 0.01) {
        raf = requestAnimationFrame(tick);
      } else {
        // Settled: drop the transform entirely so the element goes back to
        // being an ordinary, un-composited box between hovers.
        el.style.transform = '';
        el.style.willChange = '';
        raf = null;
      }
    };

    const start = () => {
      if (!raf) raf = requestAnimationFrame(tick);
    };

    // Measure once per hover, not per move. getBoundingClientRect() forces a
    // synchronous layout, and pointermove fires at pointer polling rate.
    el.addEventListener('pointerenter', () => {
      rect = el.getBoundingClientRect();
      el.style.willChange = 'transform';
    });

    el.addEventListener('pointermove', (e) => {
      if (!rect) rect = el.getBoundingClientRect();
      const xPos = (e.clientX - rect.left) / rect.width - 0.5;
      const yPos = (e.clientY - rect.top) / rect.height - 0.5;
      targetY = xPos * ROTATION_FACTOR;
      targetX = -yPos * ROTATION_FACTOR;
      start();
    });

    el.addEventListener('pointerleave', () => {
      rect = null;
      targetX = 0;
      targetY = 0;
      start();
    });
  };

  document.querySelectorAll('[data-vpx-tilt]').forEach(attach);
})();
